"use client";

import { useId, useState, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Sheet,
  SheetHeader,
  SheetTitle,
  SheetClose,
  SheetBody,
} from "@/components/ui/sheet";
import { Button, SubmitButton } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { customInstance } from "@/lib/api/client";
import { showToast } from "@/components/ui/sonner";
import { useOptionalTalentSearchFilters } from "@/components/search/talent-search-filters-provider";
import { CreatePreset } from "./create";
import { ConfirmApplyPreset } from "./confirm-apply";
import { type PresetTypeSlug } from "../lib/categories";
import { logo } from "@/lib/logo-dev";
import { Icon, type IconName } from "@/components/ui/icon";
import { IconAvatar } from "@/components/ui/icon-avatar";
import { cn } from "@/lib/utils";
import { FOCUS_RING } from "./icon-button";
import { Pagination } from "@/components/ui/pagination";

type CentralIconName = IconName;

const PRESET_APPLY_META: Record<
  PresetTypeSlug,
  {
    label: string;
    icon: CentralIconName;
    entityNoun: string;
    apiType: string;
    filterKey: string;
  }
> = {
  companies: {
    label: "Company",
    icon: "IconBuildings",
    entityNoun: "companies",
    apiType: "companies",
    filterKey: "companies",
  },
  universities: {
    label: "University",
    icon: "IconSchool",
    entityNoun: "universities",
    apiType: "universities",
    filterKey: "universities",
  },
  "job-titles": {
    label: "Job Title",
    icon: "IconBag",
    entityNoun: "job titles",
    apiType: "job_titles",
    filterKey: "job_titles",
  },
  skills: {
    label: "Skill",
    icon: "IconWhiteboard",
    entityNoun: "skills",
    apiType: "skills",
    filterKey: "skills",
  },
  investors: {
    label: "Investor",
    icon: "IconDollar",
    entityNoun: "investors",
    apiType: "investors",
    filterKey: "investors",
  },
  locations: {
    label: "Location",
    icon: "IconMapPin",
    entityNoun: "locations",
    apiType: "locations",
    filterKey: "locations",
  },
  filters: {
    label: "Filter",
    icon: "IconSettingsGear2",
    entityNoun: "filters",
    apiType: "filters",
    filterKey: "",
  },
  "job-stages": {
    label: "Job Stage",
    icon: "IconPlan",
    entityNoun: "stages",
    apiType: "job_stages",
    filterKey: "",
  },
};

export interface PresetItemResponse {
  id: string;
  name: string;
  domain?: string;
}

export interface PresetResponse {
  id: string;
  preset_type: string;
  title: string;
  description?: string;
  visibility: string;
  created_by?: string;
  created_at: string;
  items: PresetItemResponse[];
  item_count: number;
  usage_count: number;
  filters_json?: string;
}

interface PresetListResponse {
  presets: PresetResponse[];
  total: number;
}

type Tab = "my" | "shared";
const PER_PAGE = 10;

const LOGO_PRESET_TYPES: PresetTypeSlug[] = [
  "companies",
  "universities",
  "investors",
];

export interface ApplyPresetProps {
  open: boolean;
  onClose: () => void;
  presetType: PresetTypeSlug;
  /**
   * Receives the confirmed preset's items instead of writing them into the
   * talent-search filters. Set by surfaces that keep their own filter state —
   * the job pipeline panel — so the sheet works outside
   * `TalentSearchFiltersProvider`.
   */
  onApplyItems?: (items: PresetItemResponse[]) => void;
}

export function ApplyPreset({
  open,
  onClose,
  presetType,
  onApplyItems,
}: ApplyPresetProps) {
  const meta = PRESET_APPLY_META[presetType];
  const [tab, setTab] = useState<Tab>("my");
  const [page, setPage] = useState(1);
  const [createOpen, setCreateOpen] = useState(false);
  const [pendingPreset, setPendingPreset] = useState<PresetResponse | null>(
    null,
  );
  const presetListTotalId = useId();

  const talentSearch = useOptionalTalentSearchFilters();

  const selector = tab === "shared" ? "shared" : "my";

  const { data, isLoading } = useQuery<PresetListResponse>({
    queryKey: ["presets", meta.apiType, selector, page],
    queryFn: () => {
      const params = new URLSearchParams();
      params.set("preset_type", meta.apiType);
      params.set("selector", selector);
      params.set("per_page", String(PER_PAGE));
      params.set("page", String(page));
      return customInstance(`/api/presets?${params.toString()}`);
    },
    enabled: open,
  });

  const presets = data?.presets ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));

  const handleApply = useCallback(
    (preset: PresetResponse) => {
      if (presetType === "filters") {
        if (preset.filters_json && talentSearch) {
          try {
            const filters = JSON.parse(preset.filters_json);
            talentSearch.setFilters(filters);
            showToast.success(`Applied filter preset "${preset.title}"`);
            onClose();
          } catch {
            showToast.error("Failed to apply filter preset");
          }
        }
        return;
      }

      const items = preset.items;
      if (!items.length) {
        showToast.error("This preset has no items");
        return;
      }

      if (onApplyItems) {
        onApplyItems(items);
        showToast.success(`Applied "${preset.title}" to ${meta.entityNoun}`);
        onClose();
        return;
      }

      if (!talentSearch) return;
      const { updateFilter } = talentSearch;

      if (presetType === "companies") {
        const companyFilters = items.map((item) => ({
          text: item.name,
          domain: item.domain ?? undefined,
          mode: "include" as const,
          scope: "cp" as const,
          required: false,
        }));
        updateFilter("companies", companyFilters);
      } else if (presetType === "investors") {
        updateFilter(
          "investors",
          items.map((item) => ({ text: item.name, required: false, scope: "cp" as const })),
        );
      } else if (presetType === "skills") {
        updateFilter(
          "skills",
          items.map((item) => ({
            text: item.name,
            years: null,
            required: false,
          })),
        );
      } else if (presetType === "locations") {
        updateFilter(
          "locations",
          items.map((item) => ({ display_name: item.name, required: false })),
        );
      } else {
        const filterKey = meta.filterKey as "job_titles" | "universities";
        updateFilter(
          filterKey,
          items.map((item) => ({
            text: item.name,
            mode: "include" as const,
            required: false,
          })),
        );
      }

      showToast.success(`Applied "${preset.title}" to ${meta.entityNoun}`);
      onClose();
    },
    [presetType, meta, talentSearch, onApplyItems, onClose],
  );

  const handleClose = useCallback(() => {
    onClose();
    setPage(1);
    setTab("my");
  }, [onClose]);

  return (
    <>
      <Sheet
        open={open}
        onOpenChange={(nextOpen) => {
          if (nextOpen) return;
          if (pendingPreset) return;
          handleClose();
        }}
        size="lg"
        disableFocusTrap
      >
        <SheetHeader>
          <div className="flex min-w-0 flex-1 items-center gap-1.5">
            <div className="opacity-60">
              <Icon
                name="IconLayoutDashboard"
                size={18}
                className="text-heading"
              />
            </div>
            <SheetTitle>{meta.label} Presets</SheetTitle>
          </div>
          <SheetClose />
        </SheetHeader>

        <SheetBody className="gap-5">
          {/* My / Shared section */}
          <section aria-label="Your presets" className="flex flex-col gap-3">
            <h2 className="sr-only">Your presets</h2>
            <div className="flex gap-2 flex-row items-center justify-between">
              <Tabs
                value={tab}
                onValueChange={(value) => {
                  setTab(value as Tab);
                  setPage(1);
                }}
                size="sm"
              >
                <TabsList>
                  <TabsTrigger value="my">My Presets</TabsTrigger>
                  <TabsTrigger value="shared">Shared Presets</TabsTrigger>
                </TabsList>
              </Tabs>
              <SubmitButton
                type="button"
                variant="primary"
                size="sm"
                onClick={() => setCreateOpen(true)}
                icon={
                  <Icon name="IconPlusLarge" size={16} aria-hidden="true" />
                }
              >
                Create Preset
              </SubmitButton>
            </div>

            {/* Empty state */}
            {!isLoading && presets.length === 0 && (
              <div
                role="status"
                className="bg-contrast/5 border border-contrast/5 rounded-sm p-3.5 pl-5"
              >
                {/* TODO(a11y AAA-contrast): text-contrast/60 may not reach 7:1 on dark surfaces. */}
                <p className="text-sm text-contrast/60">
                  No {meta.entityNoun} presets found, create a new{" "}
                  {meta.entityNoun.replace(/s$/, "")} preset to start.
                </p>
              </div>
            )}

            {/* Loading skeleton */}
            {isLoading && (
              <div
                role="status"
                aria-label={`Loading ${meta.entityNoun} presets`}
                className="flex flex-col gap-3"
              >
                {Array.from({ length: 3 }).map((_, i) => (
                  <div
                    key={i}
                    aria-hidden="true"
                    className="bg-contrast/3 border border-contrast/5 rounded-xl p-4 shimmer shimmer-bg text-foreground motion-reduce:[animation:none!important]"
                  >
                    <div className="h-5 bg-contrast/6 rounded w-40 mb-2" />
                    <div className="h-4 bg-contrast/6 rounded w-full" />
                  </div>
                ))}
              </div>
            )}

            {/* Preset cards */}
            {!isLoading && presets.length > 0 && (
              <div className="flex flex-col gap-3">
                {presets.map((preset) => (
                  <PresetCard
                    key={preset.id}
                    preset={preset}
                    presetType={presetType}
                    onClick={() =>
                      presetType === "filters"
                        ? handleApply(preset)
                        : setPendingPreset(preset)
                    }
                  />
                ))}
              </div>
            )}

            {/* Pagination — always below cards */}
            {!isLoading && total > 0 && (
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span
                  id={presetListTotalId}
                  aria-live="polite"
                  className="text-sm font-medium text-contrast/70"
                >
                  {total} presets
                </span>
                <Pagination.Root
                  page={page}
                  totalPages={totalPages}
                  onPageChange={setPage}
                  variant="list"
                >
                  <Pagination.Nav aria-describedby={presetListTotalId}>
                    <Pagination.First />
                    <Pagination.Prev />
                    <Pagination.Pages
                      strategy="window"
                      maxVisible={4}
                      windowAlign="center"
                    />
                    <Pagination.Next />
                    <Pagination.Last />
                  </Pagination.Nav>
                </Pagination.Root>
              </div>
            )}
          </section>

          {/* Divider */}
          <div aria-hidden="true" className="h-px bg-contrast/10 shrink-0" />

          {/* Official Presets section */}
          <section
            aria-label="Official presets"
            className="flex flex-col gap-3"
          >
            <h2 className="sr-only">Official presets</h2>
            <div className="flex items-center justify-between">
              <div className="flex bg-contrast/[0.02] rounded-lg p-0.5">
                <div className="flex items-center gap-1 px-3.5 py-1 rounded-md text-sm font-medium bg-gradient-to-b from-transparent to-contrast/5 border border-contrast/5 text-heading">
                  <Icon
                    name="IconLayoutDashboard"
                    size={14}
                    aria-hidden="true"
                    className="text-heading opacity-70"
                  />
                  <span className="px-1">Official Presets</span>
                </div>
              </div>
              {/* TODO(a11y AAA-contrast): text-contrast/70 may not reach 7:1 on dark surfaces. */}
              <span className="text-sm text-contrast/70">Offered by Arbi</span>
            </div>
            <div
              role="status"
              className="bg-contrast/5 border border-contrast/5 rounded-sm p-3.5 pl-5"
            >
              {/* TODO(a11y AAA-contrast): text-contrast/60 may not reach 7:1 on dark surfaces. */}
              <p className="text-sm text-contrast/60">
                Official {meta.entityNoun} presets coming soon.
              </p>
            </div>
          </section>
        </SheetBody>
      </Sheet>

      <CreatePreset
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        presetType={presetType}
        initialFilters={
          presetType === "filters" ? talentSearch?.filters : undefined
        }
      />

      <ConfirmApplyPreset
        preset={pendingPreset}
        presetType={presetType}
        showLogo={LOGO_PRESET_TYPES.includes(presetType)}
        onCancel={() => setPendingPreset(null)}
        onConfirm={(modifiedPreset) => {
          setPendingPreset(null);
          handleApply(modifiedPreset);
        }}
      />
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Preset Card                                                         */
/* ------------------------------------------------------------------ */

const MAX_VISIBLE_PILLS = 2;

function PresetCard({
  preset,
  presetType,
  onClick,
}: {
  preset: PresetResponse;
  presetType: PresetTypeSlug;
  onClick: () => void;
}) {
  const visibleItems = preset.items.slice(0, MAX_VISIBLE_PILLS);
  const overflowCount = Math.max(0, preset.item_count - MAX_VISIBLE_PILLS);
  const showLogos = LOGO_PRESET_TYPES.includes(presetType);
  const accessibleName = preset.description
    ? `Apply preset ${preset.title}. ${preset.description}`
    : `Apply preset ${preset.title}`;

  return (
    <Button
      type="button"
      variant="secondary"
      size="sm"
      onClick={onClick}
      aria-label={accessibleName}
      className={cn(
        "h-auto w-full cursor-pointer flex-col items-stretch justify-start gap-1.5 rounded-xl border-contrast/5 bg-contrast/3 p-4 text-left font-normal shadow-none hover:border-contrast/10 hover:bg-contrast/5 active:bg-contrast/5 [&>[data-slot=button-inset]]:hidden",
        FOCUS_RING,
      )}
    >
      <div className="flex items-center gap-4 flex-wrap">
        <div className="flex min-w-0 items-center gap-2.5 flex-wrap">
          <span className="text-base font-medium text-heading">
            {preset.title}
          </span>
          {visibleItems.map((item) => (
            <EntityPill
              key={item.id}
              name={item.name}
              domain={item.domain}
              showLogo={showLogos}
            />
          ))}
        </div>
        {overflowCount > 0 && (
          <span className="text-sm text-contrast/70">
            +{overflowCount}{" "}
            {preset.preset_type === "filters"
              ? "filters"
              : preset.preset_type.replace(/_/g, " ")}
          </span>
        )}
      </div>
      {preset.description && (
        // TODO(a11y AAA-contrast): text-contrast/60 may not reach 7:1 on dark surfaces.
        <p className="text-sm text-contrast/60 line-clamp-2">
          {preset.description}
        </p>
      )}
    </Button>
  );
}

/* ------------------------------------------------------------------ */
/* Entity Pill                                                         */
/* ------------------------------------------------------------------ */

function EntityPill({
  name,
  domain,
  showLogo,
}: {
  name: string;
  domain?: string;
  showLogo?: boolean;
}) {
  const logoUrl = showLogo ? logo({ domain, name, size: 28 }) : null;

  return (
    <div className="bg-contrast/8 border border-contrast/10 rounded-lg flex items-center gap-1 px-1.5 py-1 max-h-[45px]">
      {logoUrl ? (
        <IconAvatar
          src={logoUrl}
          size={14}
          alt={name}
          className="shrink-0 rounded-[3px] shadow-[0px_2px_4px_0px_rgba(0,0,0,0.05)]"
          icon={<Icon name="IconBag" className="text-heading opacity-50" />}
        />
      ) : null}
      <span className="text-[13px] text-contrast/80 tracking-tight whitespace-nowrap">
        {name}
      </span>
    </div>
  );
}
